import React from "react";
import { Box, Button, Checkbox, Collapse, Dialog, DialogContent, FormControlLabel, IconButton, InputAdornment, MenuItem, Radio, TextField, Tooltip, Typography } from "@mui/material";
import { ChevronDown, ChevronUp, Copy, Search } from "lucide-react";
import useApi from "../../hooks/use-api";
import buildApiRequestConfig from "../../api/url-builder";
import routes from "../../api/routes";
import { useToast } from "../../providers/toaster-provider";
import CalendarInput from "../ui/calendar-input";
import WatermarkPreview from "./watermark-preview";
import ColorSwatchInput from "../ui/color-swatch-input";
import { DEFAULT_WATERMARK, DEFAULT_WATERMARK_OPTIONS, WATERMARK_POSITION_OPTIONS, WatermarkOptions, WatermarkPosition } from "../../util/watermark";

export type ShareLink = {
    id: number;
    slug: string;
    accessType: 'anonymous' | 'email_list';
    listMode: 'allow' | 'block';
    entries: string[];
    nameLabel: string | null;
    expiresAt: string | null;
    allowDownloads: boolean;
    watermarkTemplate: string | null;
    watermarkOptions: string | null;
    hasPassword: boolean;
};

// link.watermarkOptions is stored as a JSON string (document-link-model.js);
// a malformed or missing one falls back to the defaults rather than erroring,
// same as parseWatermarkOptions does server-side.
const parseStoredWatermarkOptions = (raw: string | null | undefined): WatermarkOptions => {
    if (!raw) return DEFAULT_WATERMARK_OPTIONS;
    try {
        const obj = JSON.parse(raw);
        return {
            position: WATERMARK_POSITION_OPTIONS.some(o => o.value === obj.position) ? obj.position : 'tiled',
            rotation: Number.isFinite(Number(obj.rotation)) ? Number(obj.rotation) : DEFAULT_WATERMARK_OPTIONS.rotation,
            opacity: Number.isFinite(Number(obj.opacity)) ? Number(obj.opacity) : DEFAULT_WATERMARK_OPTIONS.opacity,
            fontSize: Number.isFinite(Number(obj.fontSize)) ? Number(obj.fontSize) : DEFAULT_WATERMARK_OPTIONS.fontSize,
            fontColor: typeof obj.fontColor === 'string' ? obj.fontColor : DEFAULT_WATERMARK_OPTIONS.fontColor,
        };
    } catch {
        return DEFAULT_WATERMARK_OPTIONS;
    }
};

type Props = {
    open: boolean;
    documentSlug: string | null;
    documentTitle: string;
    // Invoice documents keep downloads, password and expiration locked.
    isInvoice?: boolean;
    // Set when editing an invoice's one share link - the invoice document's
    // own slug, not a DocumentLink. Its only editable setting is Stripe
    // payment (Document.canPay, saved via the document update route); name,
    // watermark, password and downloads don't exist on it, so they're shown
    // locked.
    invoiceShareLink?: { canPay: boolean };
    entityId: number | null;
    link: ShareLink | null; // null = creating a new link
    // Share buttons on pages whose document doesn't exist yet (report
    // shares): called on the first Save to create the document, returns its
    // slug - the new link is then created on it. documentSlug may be null.
    createDocument?: () => Promise<string>;
    // Share buttons: after creating a link, stay open showing it (so it can
    // be copied straight away) instead of leaving it to onSaved to close.
    keepOpenAfterCreate?: boolean;
    onClose: () => void;
    onSaved: () => void;
};

const TEAL = 'var(--color-primary)';
const RULE = '1px solid #e3e6ea';
const GREY_TEXT = '#8a929b';
const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const DOMAIN_RE = /^@[^\s@]+\.[^\s@]+$/;

const toDateInput = (iso: string | null): string => (iso ? new Date(iso).toISOString().slice(0, 10) : '');

// Turns what the user typed into an email ("jane@acme.com") or a domain ("@acme.com").
// A bare domain typed without "@" is accepted. Returns null when it isn't valid.
const normalizeEntry = (raw: string): string | null => {
    let value = raw.trim().toLowerCase();
    if (!value) return null;
    if (!value.includes('@')) value = '@' + value;
    if (value.startsWith('@')) return DOMAIN_RE.test(value) ? value : null;
    return EMAIL_RE.test(value) ? value : null;
};

// Label above a field. The design uses small, regular-weight labels.
const FieldLabel: React.FC<{ children: React.ReactNode }> = ({ children }) => (
    <Typography sx={{ fontSize: 11, color: '#333', mb: 0.5 }}>{children}</Typography>
);

// Compact inputs: 32px high, 11px text, light border.
const FIELD_HEIGHT = 28;
const inputSx = {
    '& .MuiInputBase-root': { height: FIELD_HEIGHT, fontSize: 11, borderRadius: '5px', background: '#fff' },
    '& .MuiInputBase-input': { py: 0, fontSize: 11 },
};

// No padding or margin on the boxes and labels, so every checkbox lines up with the
// field edges and rows centre on the same line as the fields.
const checkboxSx = { color: TEAL, '&.Mui-checked': { color: TEAL }, p: 0 };
const radioSx = { color: TEAL, '&.Mui-checked': { color: TEAL }, p: 0 };
const checkLabelSx = { m: 0, gap: 0.75, alignItems: 'center' };

// Checkbox row with an 11px label, as used throughout the dialog.
const CheckRow: React.FC<{ checked: boolean; disabled?: boolean; onChange?: (checked: boolean) => void; label: React.ReactNode }> = ({ checked, disabled, onChange, label }) => (
    <FormControlLabel
        sx={checkLabelSx}
        control={<Checkbox size="small" checked={checked} disabled={disabled} onChange={(e) => onChange?.(e.target.checked)} sx={checkboxSx} />}
        label={<Typography sx={{ fontSize: 11, lineHeight: 1.2, color: disabled ? GREY_TEXT : '#222' }}>{label}</Typography>}
    />
);

// Settings for one generated link, laid out as in the design. NDA is shown but not
// available yet. Downloads, password and expiration are available for every
// document except invoices, where they stay locked.
const DocumentLinkSettings: React.FC<Props> = ({ open, documentSlug: documentSlugProp, documentTitle, isInvoice = false, invoiceShareLink, entityId, link: linkProp, createDocument, keepOpenAfterCreate = false, onClose, onSaved }) => {
    const { showError, showSuccess } = useToast();
    const [saveApi, setSaveApi] = useApi();
    // What this modal created itself while open (share-button flows): the
    // document (createDocument) and the link (keepOpenAfterCreate). Once set,
    // the modal edits that link from then on instead of creating another.
    const [createdSlug, setCreatedSlug] = React.useState<string | null>(null);
    const [createdLink, setCreatedLink] = React.useState<ShareLink | null>(null);
    const [creatingDocument, setCreatingDocument] = React.useState(false);
    const documentSlug = createdSlug ?? documentSlugProp;
    const link = createdLink ?? linkProp;
    const isInvoiceShareLink = !!invoiceShareLink;
    const [canPay, setCanPay] = React.useState(false);
    const [nameLabel, setNameLabel] = React.useState('');
    const [accessType, setAccessType] = React.useState<'anonymous' | 'email_list'>('anonymous');
    const [listMode, setListMode] = React.useState<'allow' | 'block'>('allow');
    const [entries, setEntries] = React.useState<string[]>([]);
    const [entryInput, setEntryInput] = React.useState('');
    const [hasExpiry, setHasExpiry] = React.useState(false);
    const [expiryDate, setExpiryDate] = React.useState('');
    const [allowDownloads, setAllowDownloads] = React.useState(true);
    const [hasWatermark, setHasWatermark] = React.useState(true);
    // Advanced Options (NDA, downloads, expiration, watermark) - closed each time the modal opens.
    const [showAdvanced, setShowAdvanced] = React.useState(false);
    React.useEffect(() => { if (open) setShowAdvanced(false); }, [open]);
    const [watermark, setWatermark] = React.useState(DEFAULT_WATERMARK);
    const [watermarkOptions, setWatermarkOptions] = React.useState<WatermarkOptions>(DEFAULT_WATERMARK_OPTIONS);
    const [previewOpen, setPreviewOpen] = React.useState(false);
    const [wantsPassword, setWantsPassword] = React.useState(false);
    const [passwordInput, setPasswordInput] = React.useState('');
    const [initialSnapshot, setInitialSnapshot] = React.useState('');

    React.useEffect(() => {
        if (!open) return;
        setCreatedSlug(null);
        setCreatedLink(null);
        setCreatingDocument(false);
        const link = linkProp;
        const next = {
            nameLabel: link?.nameLabel ?? '',
            accessType: link?.accessType ?? 'anonymous',
            listMode: link?.listMode ?? 'allow',
            entries: link?.entries ?? [],
            hasExpiry: !!link?.expiresAt,
            expiryDate: toDateInput(link?.expiresAt ?? null),
            allowDownloads: link ? link.allowDownloads : true,
            // An invoice's own share link has no watermark at all.
            hasWatermark: invoiceShareLink ? false : (link ? !!link.watermarkTemplate : true),
            watermark: link?.watermarkTemplate ?? DEFAULT_WATERMARK,
            watermarkOptions: parseStoredWatermarkOptions(link?.watermarkOptions),
            wantsPassword: !!link?.hasPassword,
            passwordInput: '',
            canPay: !!invoiceShareLink?.canPay,
        };
        setCanPay(next.canPay);
        setNameLabel(next.nameLabel);
        setAccessType(next.accessType);
        setListMode(next.listMode);
        setEntries(next.entries);
        setEntryInput('');
        setHasExpiry(next.hasExpiry);
        setExpiryDate(next.expiryDate);
        setAllowDownloads(next.allowDownloads);
        setHasWatermark(next.hasWatermark);
        setWatermark(next.watermark);
        setWatermarkOptions(next.watermarkOptions);
        setWantsPassword(next.wantsPassword);
        setPasswordInput('');
        setInitialSnapshot(JSON.stringify(next));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [open, linkProp]);

    React.useEffect(() => {
        const { data, isLoading, error } = saveApi;
        if (data && !isLoading) {
            const created = !link && (data as any).link;
            showSuccess(link ? 'Link updated' : 'Link created');
            if (created && keepOpenAfterCreate) {
                // Switch to editing the link just created, so its Generated
                // Link shows (to copy) and a second Save updates it rather
                // than creating another one.
                const l = (data as any).link;
                setCreatedLink({
                    id: l.id,
                    slug: l.slug,
                    accessType: l.accessType ?? 'anonymous',
                    listMode: l.listMode === 'block' ? 'block' : 'allow',
                    entries: Array.isArray(l.entries) ? l.entries : [],
                    nameLabel: l.nameLabel ?? null,
                    expiresAt: l.expiresAt ?? null,
                    allowDownloads: l.allowDownloads !== false,
                    watermarkTemplate: l.watermarkTemplate ?? null,
                    watermarkOptions: l.watermarkOptions ?? null,
                    hasPassword: !!l.passwordHash || (wantsPassword && !!passwordInput),
                });
                setNameLabel(l.nameLabel ?? nameLabel);
                setPasswordInput('');
                setInitialSnapshot(JSON.stringify({ ...JSON.parse(currentSnapshot), nameLabel: l.nameLabel ?? nameLabel, passwordInput: '' }));
            }
            onSaved();
        }
        if (error) showError(typeof error === 'string' ? error : 'Failed to save link');
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [saveApi]);

    const addEntry = () => {
        const value = normalizeEntry(entryInput);
        if (!value) {
            showError('Enter a valid email address or domain, e.g. jane@acme.com or @acme.com');
            return;
        }
        if (!entries.includes(value)) setEntries([...entries, value]);
        setEntryInput('');
    };

    const removeEntry = (value: string) => setEntries(entries.filter(e => e !== value));

    const currentSnapshot = JSON.stringify({
        nameLabel,
        accessType,
        listMode,
        entries,
        hasExpiry,
        expiryDate,
        allowDownloads,
        hasWatermark,
        watermark,
        watermarkOptions,
        wantsPassword,
        passwordInput,
        canPay,
    });
    // A new link can always be saved; an existing one only once something changed.
    const isDirty = !link || currentSnapshot !== initialSnapshot;

    const handleSave = async () => {
        let slug = documentSlug;
        // Share-button flow for a page with no document yet (report shares):
        // create it now, on the first Save, then create the link on it.
        if (!slug && createDocument) {
            setCreatingDocument(true);
            try {
                slug = await createDocument();
                setCreatedSlug(slug);
            } catch (err: any) {
                showError(err?.message || 'Failed to create the share');
                return;
            } finally {
                setCreatingDocument(false);
            }
        }
        if (!slug) return;
        // The invoice's own share link lives on the document itself - only
        // its Stripe payment setting is editable here.
        if (isInvoiceShareLink) {
            setSaveApi({
                ...buildApiRequestConfig(routes.documents.update, [{ param: 'slug', value: slug }]),
                body: { entityId, canPay },
            });
            return;
        }
        const body: Record<string, unknown> = {
            entityId,
            nameLabel: nameLabel.trim() || null,
            accessType,
            listMode,
            entries: accessType === 'email_list' ? entries : [],
            expiresAt: hasExpiry && expiryDate ? new Date(`${expiryDate}T23:59:59`).toISOString() : null,
            allowDownloads,
            watermarkTemplate: hasWatermark ? (watermark.trim() || null) : null,
            watermarkOptions: hasWatermark ? watermarkOptions : null,
        };
        // The password is only sent when it is being set or cleared; an existing one is never re-sent.
        if (!wantsPassword) body.password = link?.hasPassword ? '' : undefined;
        else if (passwordInput) body.password = passwordInput;

        const config = link
            ? buildApiRequestConfig(routes.documents.updateLink, [
                { param: 'slug', value: slug },
                { param: 'linkId', value: link.id },
            ])
            : buildApiRequestConfig(routes.documents.createLink, [{ param: 'slug', value: slug }]);
        setSaveApi({ ...config, body });
    };

    const isSaving = !!saveApi.isLoading || creatingDocument;
    const passwordOk = !wantsPassword || (passwordInput ? passwordInput.length >= 6 : !!link?.hasPassword);
    const expiryOk = !hasExpiry || !!expiryDate;
    // An allow list with nobody on it would let no one view the link.
    const listOk = accessType !== 'email_list' || listMode === 'block' || entries.length > 0;
    const lockedTooltip = isInvoice ? 'Not available for invoices' : '';
    // Name and watermark don't exist on an invoice's own share link.
    const shareLinkLockedTooltip = isInvoiceShareLink ? 'Not available for invoice share links' : '';
    const viewerUrl = link ? `${window.location.origin}/view/${link.slug}` : '';
    const today = new Date().toISOString().slice(0, 10);

    const copyUrl = () => {
        navigator.clipboard.writeText(viewerUrl);
        showSuccess('Link copied to clipboard');
    };

    return (
        <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth PaperProps={{ sx: { borderRadius: 2 } }}>
            <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 1.5, px: 3.5, pt: 3, pb: 2.5 }}>
                <Typography sx={{ fontSize: 18, fontWeight: 700, color: TEAL }}>{documentTitle}</Typography>

                {/* Invoices only get the link itself (full width) - no name. */}
                <Box sx={{ display: 'grid', gridTemplateColumns: isInvoice ? '1fr' : '1fr 1.25fr', gap: 1.5 }}>
                    <Box>
                        <FieldLabel>Generated Link</FieldLabel>
                        <TextField
                            fullWidth
                            size="small"
                            value={viewerUrl}
                            placeholder="Created when you save"
                            disabled={!link}
                            sx={inputSx}
                            InputProps={{
                                readOnly: true,
                                endAdornment: link ? (
                                    <InputAdornment position="end">
                                        <Tooltip title="Copy link">
                                            <IconButton size="small" onClick={copyUrl}><Copy size={12} /></IconButton>
                                        </Tooltip>
                                    </InputAdornment>
                                ) : undefined,
                            }}
                        />
                    </Box>
                    {!isInvoice && (
                    <Box>
                        <FieldLabel>Name Link (not visible to visitors)</FieldLabel>
                        <Tooltip title={shareLinkLockedTooltip}>
                            <span>
                                <TextField
                                    fullWidth
                                    size="small"
                                    value={nameLabel}
                                    onChange={(e) => setNameLabel(e.target.value)}
                                    disabled={isInvoiceShareLink}
                                    inputProps={{ maxLength: 200 }}
                                    sx={inputSx}
                                />
                            </span>
                        </Tooltip>
                    </Box>
                    )}
                </Box>

                {/* Invoices show only the link and Stripe payment. */}
                {!isInvoice && (
                <Box sx={{ borderTop: RULE, pt: 1.5 }}>
                    <FieldLabel>Viewing Requirements</FieldLabel>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'nowrap' }}>
                        {/* "Require an email to view" opens the allow/block list
                            below; visitors then verify their email before viewing. */}
                        <TextField
                            select
                            size="small"
                            value={accessType}
                            onChange={(e) => setAccessType(e.target.value as 'anonymous' | 'email_list')}
                            sx={{ ...inputSx, width: 170, flexShrink: 0 }}
                        >
                            <MenuItem value="anonymous" sx={{ fontSize: 11 }}>Anyone with the link</MenuItem>
                            <MenuItem value="email_list" sx={{ fontSize: 11 }}>Require an email to view</MenuItem>
                        </TextField>
                        <Tooltip title={lockedTooltip}>
                            <span>
                                <CheckRow checked={wantsPassword} disabled={isInvoice} onChange={setWantsPassword} label="Add password protection" />
                            </span>
                        </Tooltip>
                    </Box>
                </Box>
                )}

                {/* Invoices only - whether a visitor can pay the invoice
                    online. Was the invoice Share dialog's own toggle before
                    every Share button moved to this modal. */}
                {isInvoice && (
                    <Box sx={{ borderTop: RULE, pt: 1.5 }}>
                        <CheckRow
                            checked={canPay}
                            onChange={setCanPay}
                            disabled={!isInvoiceShareLink}
                            label="Allow payment via Stripe"
                        />
                        <Typography sx={{ fontSize: 10, color: GREY_TEXT, mt: 0.5, ml: 3 }}>
                            Anyone with this link can pay the invoice online. Needs Stripe connected for this company (Integrations).
                        </Typography>
                    </Box>
                )}

                {wantsPassword && (
                    <Box sx={{ width: 220 }}>
                        <TextField
                            fullWidth
                            size="small"
                            type="password"
                            value={passwordInput}
                            onChange={(e) => setPasswordInput(e.target.value)}
                            placeholder={link?.hasPassword ? 'Leave blank to keep the current password' : 'Password (6 characters or more)'}
                            inputProps={{ maxLength: 100, autoComplete: 'new-password' }}
                            sx={inputSx}
                        />
                    </Box>
                )}

                {accessType === 'email_list' && (
                    <Box sx={{ border: `1px solid ${TEAL}`, borderRadius: 1.5, p: 2, display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                        <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2 }}>
                            <Box>
                                <FormControlLabel
                                    sx={checkLabelSx}
                                    checked={listMode === 'allow'}
                                    onChange={() => setListMode('allow')}
                                    control={<Radio size="small" sx={radioSx} />}
                                    label={<Typography sx={{ fontSize: 11, fontWeight: 600, color: TEAL }}>Allow certain emails or domains</Typography>}
                                />
                                <Typography sx={{ fontSize: 9, color: GREY_TEXT, ml: 3.5, mt: -0.5 }}>Block everyone else from viewing</Typography>
                            </Box>
                            <Box>
                                <FormControlLabel
                                    sx={checkLabelSx}
                                    checked={listMode === 'block'}
                                    onChange={() => setListMode('block')}
                                    control={<Radio size="small" sx={radioSx} />}
                                    label={<Typography sx={{ fontSize: 11, fontWeight: 600, color: TEAL }}>Block certain emails or domains</Typography>}
                                />
                                <Typography sx={{ fontSize: 9, color: GREY_TEXT, ml: 3.5, mt: -0.5 }}>Allow anyone else to view</Typography>
                            </Box>
                        </Box>

                        <Box>
                            <Typography sx={{ fontSize: 11, fontWeight: 600 }}>
                                Specify email addresses or domains{' '}
                                <Typography component="span" sx={{ fontSize: 9, color: GREY_TEXT, fontWeight: 400 }}>
                                    (Add multiple email addresses and/or domains and press "Enter")
                                </Typography>
                            </Typography>
                            <TextField
                                fullWidth
                                size="small"
                                value={entryInput}
                                onChange={(e) => setEntryInput(e.target.value)}
                                onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); addEntry(); } }}
                                placeholder="jane@acme.com or @acme.com"
                                sx={{ ...inputSx, mt: 0.75 }}
                                InputProps={{ startAdornment: <InputAdornment position="start"><Search size={12} /></InputAdornment> }}
                            />
                        </Box>

                        <Box>
                            <Typography sx={{ fontSize: 10, fontWeight: 600, mb: 0.25 }}>
                                {listMode === 'allow' ? 'Allow List' : 'Block List'} ({entries.length})
                            </Typography>
                            {entries.length === 0 ? (
                                <Typography sx={{ fontSize: 10, color: GREY_TEXT, py: 0.5, borderTop: RULE }}>
                                    No entries yet. Add an email address or a domain above.
                                </Typography>
                            ) : (
                                <Box sx={{ maxHeight: 150, overflowY: 'auto' }}>
                                    {entries.map(value => (
                                        <Box key={value} sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', minHeight: 20, px: 1, borderTop: '1px solid #eef0f2' }}>
                                            <Typography sx={{ fontSize: 10 }}>{value}</Typography>
                                            <IconButton size="small" onClick={() => removeEntry(value)} aria-label={`Remove ${value}`} sx={{ p: 0.25 }}>
                                                <Typography sx={{ fontSize: 10, color: GREY_TEXT }}>x</Typography>
                                            </IconButton>
                                        </Box>
                                    ))}
                                </Box>
                            )}
                        </Box>
                    </Box>
                )}

                {/* Everything past the basics, tucked away until asked for. */}
                {!isInvoice && (
                    <Box sx={{ borderTop: RULE, pt: 1.5, display: 'flex', flexDirection: 'column', gap: 1 }}>
                        <Box
                            onClick={() => setShowAdvanced(v => !v)}
                            sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', cursor: 'pointer', userSelect: 'none' }}
                        >
                            <Typography sx={{ fontSize: 12, fontWeight: 600, color: TEAL }}>Advanced Options</Typography>
                            {showAdvanced ? <ChevronUp size={14} color={TEAL} /> : <ChevronDown size={14} color={TEAL} />}
                        </Box>
                        <Collapse in={showAdvanced} unmountOnExit>
                            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, pt: 0.5 }}>
                                <Tooltip title="Not available yet">
                                    <span>
                                        <CheckRow checked={false} disabled label="Require visitors to sign an NDA" />
                                    </span>
                                </Tooltip>
                        <Tooltip title={lockedTooltip}>
                            <span>
                                <CheckRow checked={allowDownloads} disabled={isInvoice} onChange={setAllowDownloads} label="Allow downloads" />
                            </span>
                        </Tooltip>

                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                            <Tooltip title={lockedTooltip}>
                                <span>
                                    <CheckRow checked={hasExpiry} disabled={isInvoice} onChange={setHasExpiry} label="Set an expiration date" />
                                </span>
                            </Tooltip>
                            <Box sx={{ width: 170 }}>
                                <CalendarInput
                                    value={expiryDate}
                                    onChange={setExpiryDate}
                                    disabled={isInvoice || !hasExpiry}
                                    minDate={today}
                                    placeholder="mm/dd/yyyy"
                                    disableStartAdornment
                                    sx={{ '& .MuiInputBase-root': { height: FIELD_HEIGHT } }}
                                />
                            </Box>
                        </Box>

                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                            <Tooltip title={shareLinkLockedTooltip}>
                                <span>
                                    <CheckRow
                                        checked={hasWatermark}
                                        onChange={setHasWatermark}
                                        disabled={isInvoiceShareLink}
                                        label="Add a watermark to protect from unauthorized use"
                                    />
                                </span>
                            </Tooltip>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                                <TextField
                                    fullWidth
                                    size="small"
                                    value={watermark}
                                    disabled={!hasWatermark}
                                    onChange={(e) => setWatermark(e.target.value)}
                                    inputProps={{ maxLength: 500 }}
                                    sx={{ ...inputSx, '& .MuiInputBase-root': { height: 28, fontSize: 11 } }}
                                />
                                <WatermarkPreview
                                    open={previewOpen}
                                    template={watermark}
                                    options={watermarkOptions}
                                    onApply={(t, o) => { setWatermark(t); setWatermarkOptions(o); setPreviewOpen(false); }}
                                    onClose={() => setPreviewOpen(false)}
                                />
                                <Typography
                                    onClick={() => hasWatermark && setPreviewOpen(true)}
                                    sx={{
                                        fontSize: 10,
                                        whiteSpace: 'nowrap',
                                        color: hasWatermark ? TEAL : GREY_TEXT,
                                        cursor: hasWatermark ? 'pointer' : 'default',
                                        textDecoration: hasWatermark ? 'underline' : 'none',
                                    }}
                                >
                                    Preview and Customize
                                </Typography>
                            </Box>

                            {hasWatermark && (
                                <Box sx={{ display: 'flex', alignItems: 'flex-end', gap: 1.5, flexWrap: 'wrap' }}>
                                    <Box sx={{ width: 150 }}>
                                        <FieldLabel>Position</FieldLabel>
                                        <TextField
                                            select
                                            fullWidth
                                            size="small"
                                            value={watermarkOptions.position}
                                            onChange={(e) => setWatermarkOptions({ ...watermarkOptions, position: e.target.value as WatermarkPosition })}
                                            sx={inputSx}
                                        >
                                            {WATERMARK_POSITION_OPTIONS.map(o => (
                                                <MenuItem key={o.value} value={o.value} sx={{ fontSize: 11 }}>{o.label}</MenuItem>
                                            ))}
                                        </TextField>
                                    </Box>
                                    <Box sx={{ width: 90 }}>
                                        <FieldLabel>Rotation (°)</FieldLabel>
                                        <TextField
                                            type="number"
                                            fullWidth
                                            size="small"
                                            value={watermarkOptions.rotation}
                                            onChange={(e) => setWatermarkOptions({ ...watermarkOptions, rotation: Math.max(-180, Math.min(180, Number(e.target.value) || 0)) })}
                                            inputProps={{ min: -180, max: 180, step: 5 }}
                                            sx={inputSx}
                                        />
                                    </Box>
                                    <Box sx={{ width: 90 }}>
                                        <FieldLabel>Opacity</FieldLabel>
                                        <TextField
                                            type="number"
                                            fullWidth
                                            size="small"
                                            value={watermarkOptions.opacity}
                                            onChange={(e) => setWatermarkOptions({ ...watermarkOptions, opacity: Math.max(0.05, Math.min(1, Number(e.target.value) || 0.05)) })}
                                            inputProps={{ min: 0.05, max: 1, step: 0.05 }}
                                            sx={inputSx}
                                        />
                                    </Box>
                                    <Box sx={{ width: 90 }}>
                                        <FieldLabel>Font Size</FieldLabel>
                                        <TextField
                                            type="number"
                                            fullWidth
                                            size="small"
                                            value={watermarkOptions.fontSize}
                                            onChange={(e) => setWatermarkOptions({ ...watermarkOptions, fontSize: Math.max(6, Math.min(72, Number(e.target.value) || 6)) })}
                                            inputProps={{ min: 6, max: 72, step: 1 }}
                                            sx={inputSx}
                                        />
                                    </Box>
                                    <Box sx={{ width: 70 }}>
                                        <FieldLabel>Color</FieldLabel>
                                        <ColorSwatchInput
                                            value={watermarkOptions.fontColor}
                                            onChange={(color) => setWatermarkOptions(prev => ({ ...prev, fontColor: color }))}
                                            size={FIELD_HEIGHT}
                                            ariaLabel="Pick watermark color"
                                        />
                                    </Box>
                                </Box>
                            )}
                        </Box>
                            </Box>
                        </Collapse>
                    </Box>
                )}

                <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 2, pt: 1 }}>
                    <Button
                        variant="outlined"
                        onClick={onClose}
                        sx={{ textTransform: 'uppercase', fontSize: 11, fontWeight: 600, width: 122, height: 34, borderRadius: '4px', borderColor: '#bfc5cc', color: '#444' }}
                    >
                        Close
                    </Button>
                    <Button
                        variant="contained"
                        onClick={handleSave}
                        disabled={isSaving || (!documentSlug && !createDocument) || !isDirty || !passwordOk || !expiryOk || !listOk}
                        sx={{ textTransform: 'uppercase', fontSize: 11, fontWeight: 600, width: 122, height: 34, borderRadius: '4px', boxShadow: 'none', background: TEAL, '&:hover': { background: 'var(--color-primary-hover)', boxShadow: 'none' } }}
                    >
                        Save
                    </Button>
                </Box>
            </DialogContent>
        </Dialog>
    );
};

export default DocumentLinkSettings;
